import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Updated to use TestProtocol from types/index.ts
import { TestProtocol, TargetingCondition } from '../types'; // Added TargetingCondition
import { TargetingConditionChip } from './common';

interface TestProtocolManagerProps {
  protocols: TestProtocol[];
  onProtocolsChange: (protocols: TestProtocol[]) => void; // Renamed from onChange for clarity
  availableTargetingConditions: TargetingCondition[]; // Added
  disabled?: boolean;
}

const TestProtocolManager: React.FC<TestProtocolManagerProps> = ({
  protocols = [],
  onProtocolsChange, // Renamed from onChange
  availableTargetingConditions = [], // Added default value
  disabled = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  // State for the form fields based on TestProtocol interface
  const [protocolName, setProtocolName] = useState('');
  const [protocolContent, setProtocolContent] = useState('');
  const [conditionNames, setConditionNames] = useState<string[]>([]); // Stores as array of strings
  // const [conditionNamesInput, setConditionNamesInput] = useState(''); // Removed, replaced by multi-select

  const [nameError, setNameError] = useState('');
  const [contentError, setContentError] = useState('');
  const [conditionNamesError, setConditionNamesError] = useState('');

  // Open dialog for adding new protocol
  const handleAddClick = () => {
    setEditIndex(null);
    setProtocolName('');
    setProtocolContent('');
    setConditionNames([]); // Reset to empty array for multi-select
    // setConditionNamesInput(''); // Removed
    setNameError('');
    setContentError('');
    setConditionNamesError('');
    setDialogOpen(true);
  };

  // Open dialog for editing existing protocol
  const handleEditClick = (index: number) => {
    const protocol = protocols[index];
    setEditIndex(index);
    setProtocolName(protocol.name);
    setProtocolContent(protocol.protocol_content);
    setConditionNames(protocol.condition_names); // Already an array, suitable for multi-select
    // setConditionNamesInput(protocol.condition_names.join(', ')); // Removed
    setNameError('');
    setContentError('');
    setConditionNamesError('');
    setDialogOpen(true);
  };

  // Handle deleting a protocol
  const handleDeleteClick = (index: number) => {
    const updatedProtocols = [...protocols];
    updatedProtocols.splice(index, 1);
    onProtocolsChange(updatedProtocols); // Use onProtocolsChange
  };

  // Validate JSON
  const validateJson = (json: string): boolean => {
    try {
      JSON.parse(json);
      return true;
    } catch {
      // Invalid JSON format
      return false;
    }
  };

  // Handle save in the dialog
  const handleSave = () => {
    let isValid = true;
    setNameError('');
    setContentError('');
    setConditionNamesError('');

    if (!protocolName.trim()) {
      setNameError('Protocol name is required');
      isValid = false;
    }

    if (!protocolContent.trim()) {
      setContentError('Protocol content is required (JSON format)');
      isValid = false;
    } else if (!validateJson(protocolContent)) {
      setContentError('Invalid JSON format for protocol content');
      isValid = false;
    }

    if (conditionNames.length === 0) {
      setConditionNamesError('At least one condition name is required');
      isValid = false;
    }

    if (isValid) {
      const newProtocol: TestProtocol = {
        name: protocolName.trim(),
        protocol_content: protocolContent.trim(),
        condition_names: conditionNames, // Use conditionNames directly
      };

      const updatedProtocols = [...protocols];
      if (editIndex !== null) {
        updatedProtocols[editIndex] = newProtocol;
      } else {
        updatedProtocols.push(newProtocol);
      }
      onProtocolsChange(updatedProtocols); // Use onProtocolsChange
      setDialogOpen(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Test Protocols
          <Tooltip title="Define test protocols. A Test Protocol is a JSON file that specifies data collection instructions for target devices.">
            <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
          </Tooltip>
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={disabled}
          size="small"
        >
          Add Protocol
        </Button>
      </Box>
      {protocols.length === 0 && !disabled && (
        <Typography variant="body2" color="text.secondary">
          No test protocols defined. Click &quot;Add Protocol&quot; to create one.
        </Typography>
      )}
      {protocols.length === 0 && disabled && (
        <Typography variant="body2" color="text.secondary">
          No test protocols.
        </Typography>
      )}
      <List dense>
        {protocols.map((protocol, index) => (
          <ListItem
            key={protocol.name ?? `protocol-${index}`}
            divider
            sx={{
              mb: 1,
              p: 1.5,
              bgcolor: 'background.paper',
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  width: '100%',
                  mb: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                    {protocol.name}
                  </Typography>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Targeting Conditions:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {protocol.condition_names.length > 0 ? (
                        protocol.condition_names.map(name => {
                          const condition = availableTargetingConditions.find(
                            tc => tc.name === name
                          );
                          return condition ? (
                            <TargetingConditionChip key={name} condition={condition} size="small" />
                          ) : (
                            <Chip
                              key={name}
                              label={name}
                              size="small"
                              sx={{ backgroundColor: 'grey.300', color: 'text.secondary' }}
                            />
                          );
                        })
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No targeting conditions
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                {!disabled && (
                  <Box>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        handleEditClick(index);
                      }}
                      size="small"
                      aria-label="edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteClick(index);
                      }}
                      size="small"
                      aria-label="delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
              <Accordion sx={{ mt: 1, boxShadow: 'none', '&:before': { display: 'none' } }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                  sx={{
                    minHeight: 'auto',
                    '& .MuiAccordionSummary-content': { margin: '8px 0' },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    View Protocol Content (JSON)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    Protocol Content (JSON):
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      backgroundColor: 'grey.50',
                      p: 1,
                      borderRadius: 1,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      mt: 0.5,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    }}
                  >
                    {protocol.protocol_content}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </ListItem>
        ))}
      </List>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editIndex !== null ? 'Edit Test Protocol' : 'Add New Test Protocol'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="protocolName"
            label="Protocol Name"
            type="text"
            fullWidth
            variant="outlined"
            value={protocolName}
            onChange={e => setProtocolName(e.target.value)}
            error={!!nameError}
            helperText={
              nameError ||
              'Enter a descriptive name for this protocol. Examples: "High-Frequency Accelerometer", "Heart Rate Variability Study", "GPS Tracking for Running"'
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="protocolContent"
            label="Protocol Content (JSON format)"
            type="text"
            fullWidth
            multiline
            rows={8}
            variant="outlined"
            value={protocolContent}
            onChange={e => setProtocolContent(e.target.value)}
            error={!!contentError}
            helperText={
              contentError ||
              'Enter the JSON content for this protocol. This defines sensor data collection parameters for Polar devices. Example: {"sensors": ["accelerometer", "heart_rate"], "sampling_rate": 100, "duration_minutes": 30}'
            }
            sx={{ mb: 2 }}
            placeholder={`{
  "sensors": ["accelerometer", "heart_rate", "gps"],
  "sampling_rate": 100,
  "duration_minutes": 30,
  "device_specific": {
    "vantagev3": {
      "high_precision": true
    }
  }
}`}
          />
          {/* Replace TextField for conditionNames with a Multi-Select Dropdown */}
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }} error={!!conditionNamesError}>
            <InputLabel id="condition-names-select-label">Targeting Conditions</InputLabel>
            <Select
              labelId="condition-names-select-label"
              multiple
              value={conditionNames}
              onChange={e => setConditionNames(e.target.value as string[])}
              input={<OutlinedInput label="Targeting Conditions" />}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(value => {
                    const condition = availableTargetingConditions.find(tc => tc.name === value);
                    return condition ? (
                      <TargetingConditionChip key={value} condition={condition} />
                    ) : (
                      <Chip key={value} label={value} />
                    );
                  })}
                </Box>
              )}
            >
              {availableTargetingConditions.map(condition => (
                <MenuItem key={condition.name} value={condition.name}>
                  <TargetingConditionChip condition={condition} size="small" sx={{ mr: 1 }} />
                  {condition.name}
                </MenuItem>
              ))}
            </Select>
            {conditionNamesError && (
              <Typography color="error" variant="caption">
                {conditionNamesError}
              </Typography>
            )}
            {!conditionNamesError && (
              <Typography variant="caption" color="text.secondary">
                Select targeting conditions that determine which Polar device users this protocol
                applies to. The protocol will only be sent to devices that match these user and
                device criteria.
              </Typography>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TestProtocolManager;
